import type { ReactTable, RowData, TableFeatures } from '@tanstack/react-table';
import clsx from 'clsx';
import { useContext, useRef, type JSX } from 'react';
import { useIntl } from 'react-intl';

import Colors from '@dynatrace/strato-design-tokens/colors';
import Spacings from '@dynatrace/strato-design-tokens/spacings';
import { MinusIcon, PlusIcon } from '@dynatrace/strato-icons';

import {
  DATA_TABLE_SUB_ROW_DENSITY_PADDING_MAP as SUB_ROW_DENSITY_PADDING_MAP,
  DATA_TABLE_SUB_ROW_EXPANDER_ICON_SIZE as SUB_ROW_EXPANDER_ICON_SIZE,
  DATA_TABLE_SUB_ROW_INDENT_GRID as SUB_ROW_INDENT_GRID,
} from '../../../constants.js';
import * as styles from '../SubRows.sty.js';
import { SubrowCellContext } from './SubRowCellContext.js';
import { SubRowExpanderPath } from './SubRowExpanderPath.js';
import {
  useDataTable,
  useDataTableRow,
} from '../../../contexts/DataTableContext.js';
import { dataTableMessages } from '../../../messages.js';
import {
  isChartColumnType,
  type DataTableColumnType,
} from '../../ColumnTypes/column-types-types.js';

/**
 * Calculates the width for the subrow indicator cell based on the current indent width.
 * @param rowDensity - the configured row density of the table
 * @param indentWidth - the current indent width based on the subrow depth
 * @internal
 */
export function getSubRowCellWidth(
  rowDensity: 'default' | 'condensed' | 'comfortable',
  indentWidth: number,
): string {
  const rowDensityPadding = SUB_ROW_DENSITY_PADDING_MAP[rowDensity];

  // Calculate the cell width by adding the icon button size (icon size + 2 * Size2 padding)
  // and the density padding to the right
  return `calc(${
    indentWidth + SUB_ROW_EXPANDER_ICON_SIZE + rowDensityPadding
  }px + ${Spacings.Size4})`;
}

function remeasureSubRowIndicatorColumn<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(table: ReactTable<TFeatures, TData>) {
  const subrowIndicatorColumnId = table.getSubRowIndicatorColumnId();
  if (subrowIndicatorColumnId) {
    table.remeasureColumn(subrowIndicatorColumnId);
  }
}

/**
 * @internal
 */
export function SubRowsHeaderExpander(
  props: Readonly<{ isAllRowsExpanded: boolean }>,
): JSX.Element {
  const { isAllRowsExpanded } = props;
  const table = useDataTable();
  const intl = useIntl();

  return (
    <button
      type="button"
      className={clsx(
        styles.expandButtonHeaderCellWrapper,
        styles.expanderButton,
      )}
      onClick={() => {
        table.toggleAllRowsExpanded();
        remeasureSubRowIndicatorColumn(table);
      }}
      aria-label={intl.formatMessage(dataTableMessages.subRowAllTrigger, {
        expandState: isAllRowsExpanded ? 'other' : 'expand',
      })}
      style={{
        color: Colors.Text.Neutral.Subdued,
      }}
      data-dt-name="SubRowExpandCollapseAll"
      data-dt-properties={`state:${isAllRowsExpanded ? 'collapse' : 'expand'}`}
    >
      {isAllRowsExpanded ? <MinusIcon /> : <PlusIcon />}
    </button>
  );
}

/**
 * @internal
 */
export function SubRowsCellExpander({
  columnType,
}: {
  columnType: DataTableColumnType | undefined;
}): JSX.Element | null {
  const intl = useIntl();
  const table = useDataTable();
  const row = useDataTableRow();

  const subrowContext = useContext(SubrowCellContext);
  const subRowExpandButtonWrapperRef = useRef<HTMLDivElement>(null);

  if (!row) {
    return null;
  }

  const {
    canExpand: rowCanExpand,
    canExpandDisabled: rowCanExpandDisabled,
    isExpanded,
  } = subrowContext;

  const indentWidth = SUB_ROW_INDENT_GRID * row.depth;
  const shouldCorrectIndicatorWidth = isChartColumnType(columnType);

  const subRowDepth = table.getSubRowOpenDepth();
  const depthDifference = subRowDepth - row.depth;
  const widthCorrection = SUB_ROW_INDENT_GRID * depthDifference;
  let width = getSubRowCellWidth(
    table.atoms.visualVariantConfig?.get().rowDensity,
    indentWidth,
  );
  if (shouldCorrectIndicatorWidth) {
    width = `calc(${width} + ${widthCorrection}px)`;
  }

  return (
    <div
      ref={subRowExpandButtonWrapperRef}
      className={clsx(styles.expandButtonCellWrapper)}
      style={{
        width,
      }}
    >
      {row.depth > 0 ? (
        <SubRowExpanderPath
          rowId={row.id}
          width={indentWidth}
          rowLineConfig={table.getRowLineConfig(row.id)}
          rowCanExpand={rowCanExpand}
        />
      ) : null}

      {/* Only render a button if the row can actually expand */}
      {rowCanExpand ? (
        <button
          disabled={rowCanExpandDisabled}
          type="button"
          aria-label={intl.formatMessage(dataTableMessages.subRowTrigger, {
            expandState: isExpanded ? 'other' : 'expand',
            id: row.id,
          })}
          aria-expanded={isExpanded}
          className={styles.expanderButton}
          onClick={() => {
            row.toggleExpanded(!isExpanded);
            remeasureSubRowIndicatorColumn(table);
          }}
          data-dt-name="SubRowExpandCollapse"
          data-dt-properties={`state:${isExpanded ? 'collapse' : 'expand'}`}
        >
          {isExpanded ? (
            <MinusIcon size={SUB_ROW_EXPANDER_ICON_SIZE} />
          ) : (
            <PlusIcon size={SUB_ROW_EXPANDER_ICON_SIZE} />
          )}
        </button>
      ) : null}

      {shouldCorrectIndicatorWidth ? (
        <div style={{ width: widthCorrection }} />
      ) : null}
    </div>
  );
}
